import React, { useEffect, useState } from 'react';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { CreateProjectStepsTitle } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStepsTitle/CreateProjectStepsTitle';
import { Button } from 'src/components/ui/button';
import {
  useGetKnowledgeBaseQuery,
  useLazyGetCollectorsStatusQuery,
  useRunKnowledgeBaseCollectorMutation,
} from 'src/services';
import { useLoader } from 'src/provider/LoaderProvider';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from 'src/store';
import { useDispatch } from 'react-redux';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { CreateProjectHead } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectHead/CreateProjectHead';
import { UseExistingKBAccordion } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStep2Page/UseExistingKBAccordion/UseExistingKBAccordion';
import { CreateNewKBAccordion } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStep2Page/CreateNewKBAccordion/CreateNewKBAccordion';
import { Separator } from 'src/components/ui/separator';
import { ProductsType } from 'src/constants/products';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import { API_VERSION } from 'src/constants/api-routes';

const filterKnowledgeBases = (items, excludeId) => {
  return items.filter((item) => `${item.id}` !== `${excludeId}`);
};

const findNewKnowledgeBase = (items, kbId) => {
  return items.find((item) => `${item.id}` === `${kbId}`);
};

const CreateProjectStep2Page = () => {
  const loader = useLoader();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const navigate = useNavigate();
  const { id, step } = useParams();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const creationFlowState = useAppSelector((s) => s.creationFlowSlices);
  const dispatch = useDispatch();
  const projectTypeVar = projectType || type;
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [newKnowledgeBase, setNewKnowledgeBase] = useState(null);
  const [isContinueDisabled, setIsContinueDisabled] = useState(true);
  const [collectors, setCollectors] = useState([]);
  const { data: knowledgeBaseData } = useGetKnowledgeBaseQuery(
    {
      load_projects: true,
      page_size: 100,
    },
    { refetchOnMountOrArgChange: true, skip: !id },
  ) as any;
  const [fetchCollectorsStatus] = useLazyGetCollectorsStatusQuery();
  const [runKnowledgeBaseCollector] = useRunKnowledgeBaseCollectorMutation();

  useEffect(() => {
    const config = creationFlowState[id];

    if (!config || (config && +config.step < +step)) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
          formData: null,
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (knowledgeBaseData) {
      const config = creationFlowState[id];
      const excludeKbId = config?.knowledgeBaseId;

      if (excludeKbId) {
        setKnowledgeBases(filterKnowledgeBases(knowledgeBaseData.items, excludeKbId));
        setNewKnowledgeBase(findNewKnowledgeBase(knowledgeBaseData.items, excludeKbId));
      } else {
        setKnowledgeBases(knowledgeBaseData.items);
        setNewKnowledgeBase(null);
      }
      loader.hide();
    }
  }, [knowledgeBaseData, creationFlowState]);

  useEffect(() => {
    if (newKnowledgeBase) {
      setIsContinueDisabled(false);
      return;
    }

    if (knowledgeBases && knowledgeBases.length) {
      const isContinueEnabled = knowledgeBases.some((item) => {
        return item.projects.some((project) => `${project.id}` === `${id}`);
      });

      setIsContinueDisabled(!isContinueEnabled);
    }
  }, [knowledgeBases, newKnowledgeBase]);

  const next = async () => {
    const promises = [];
    setIsContinueDisabled(true);
    const version = localStorage.getItem('API_VERSION') || API_VERSION.V1;

    if (collectors.length > 0) {
      for (let i = 0; i < collectors.length; i++) {
        const collector = collectors[i];

        if (version === API_VERSION.V1 && collector) {
          const { data } = await fetchCollectorsStatus({ collectorId: collector.id });

          if (['idle', 'failed'].includes((data as any).status)) {
            const promise = runKnowledgeBaseCollector({
              knowledgeBaseId: newKnowledgeBase.id,
              collectorId: collector.id,
            });

            promises.push(promise);
          }
        }

        if (
          version === API_VERSION.V2 &&
          collector &&
          ['zendesk_tickets', 'zendesk_helpcenter', 'freshdesk_tickets', 'web_scrapper'].includes(
            collector.type,
          )
        ) {
          const promise = runKnowledgeBaseCollector({
            knowledgeBaseId: newKnowledgeBase.id,
            collectorId: collector.id,
          });

          promises.push(promise);
        }
      }
    }

    await Promise.all(promises);
    setIsContinueDisabled(false);

    if (version === API_VERSION.V2) {
      dispatch(
        setCreationFlow({
          id: id,
          promptType: 'system',
        }),
      );
    }

    navigate(
      projectTypeVar === ProductsType.customer
        ? `${PATH_DASHBOARD.PROJECTS}/${id}/new/${version === API_VERSION.V2 ? +step + 2 : +step + 1}?type=${projectTypeVar}`
        : `${PATH_DASHBOARD.PROJECTS}/${id}?type=${projectTypeVar}`,
      { replace: true },
    );
  };

  const onCollectorsChanged = (collectors) => {
    setCollectors(collectors);
  };

  const back = () => {
    navigate(
      projectTypeVar === ProductsType.customer
        ? `${PATH_DASHBOARD.PROJECTS}/${id}/new/${+step - 1}?type=${projectTypeVar}`
        : `${PATH_DASHBOARD.PROJECTS}/${id}?type=${projectTypeVar}`,
      { replace: true },
    );
  };

  return (
    <React.Fragment>
      <PageContentWrapper
        menuBarVisible={false}
        className={'create-project-step items-center !gap-5'}
      >
        <CreateProjectStepsTitle step={+step} title={'AI Name'} />
        <CreateProjectHead step={+step} titleIndex={1} />
        {Boolean(knowledgeBases.length) && (
          <div className={'flex flex-col w-[455px]'}>
            <UseExistingKBAccordion items={knowledgeBases} />
            <Separator className={'bg-[#E3E6E8] mt-5'} />
          </div>
        )}
        <div className={'flex flex-col w-full items-center'}>
          <CreateNewKBAccordion item={newKnowledgeBase} onCollectorsChanged={onCollectorsChanged} />
        </div>
        <div className={'w-[455px] flex flex-col gap-5 items-center'}>
          <Button
            disabled={isContinueDisabled}
            onClick={next}
            className="min-w-24 w-full flex flex-row h-12"
          >
            Continue
          </Button>
          <div
            onClick={back}
            className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
          >
            ← Back
          </div>
        </div>
      </PageContentWrapper>
    </React.Fragment>
  );
};

export default CreateProjectStep2Page;
